import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { EditIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getEmployees } from '../../hooks/employees';
import { getTask, updateTaskDetails } from '../../hooks/tasks';
import { Employee, Task } from '../../utils/types';
import { TaskFormFields, TaskFormValues } from './components/taskFormShared';
import {
  formatTaskDate,
  formatTaskDateTime,
  TaskHero,
  TaskPrimaryButton,
  TaskPriorityBadge,
  TaskSecondaryButton,
  TaskSectionCard,
  TaskShell,
  TaskStatusBadge,
} from './components/taskUi';

const mapTaskToFormValues = (task: Task): TaskFormValues => ({
  title: task.title || '',
  description: task.description || '',
  start_date: task.start_date || '',
  due_date: task.due_date || '',
  priority: (task.priority || 'medium') as TaskFormValues['priority'],
  status: (task.status || 'pending') as TaskFormValues['status'],
  assigned_to: task.assigned_to || '',
  remarks: task.remarks || '',
});

const EditTaskPage = () => {
  const toast = useToast();
  const { orgId, taskId } = useParams<{ orgId: string; taskId: string }>();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [task, setTask] = useState<Task | null>(null);
  const [formData, setFormData] = useState<TaskFormValues | null>(null);
  const [originalData, setOriginalData] = useState<TaskFormValues | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!orgId || !taskId) {
        return;
      }

      setLoading(true);
      try {
        const [taskResponse, employeesResponse] = await Promise.all([
          getTask(orgId, taskId),
          getEmployees(orgId),
        ]);

        if (taskResponse?.success && taskResponse.data) {
          const nextTask = taskResponse.data as Task;
          const mapped = mapTaskToFormValues(nextTask);
          setTask(nextTask);
          setFormData(mapped);
          setOriginalData(mapped);
        } else {
          toast({ status: 'error', title: 'Failed to load task data' });
        }

        if (employeesResponse?.success && Array.isArray(employeesResponse.data)) {
          setEmployees(employeesResponse.data);
        } else {
          setEmployees([]);
        }
      } catch {
        toast({ status: 'error', title: 'Failed to load task data' });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [orgId, taskId, toast]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = event.target;
    setFormData((current) => (current ? { ...current, [name]: value } : current));
  };

  const changedCount = useMemo(() => {
    if (!formData || !originalData) {
      return 0;
    }

    return Object.entries(formData).filter(
      ([key, value]) => originalData[key as keyof TaskFormValues] !== value,
    ).length;
  }, [formData, originalData]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!orgId || !taskId || !formData || !originalData) {
      return;
    }

    const changes = Object.entries(formData).reduce((acc, [key, value]) => {
      if (originalData[key as keyof TaskFormValues] !== value) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, string>);

    if (Object.keys(changes).length === 0) {
      toast({ status: 'info', title: 'No changes to save' });
      return;
    }

    setSaving(true);
    const response = await updateTaskDetails(orgId, taskId, changes);
    setSaving(false);

    if (response?.success === true) {
      toast({
        status: 'success',
        title: 'Task updated',
        description: `${Object.keys(changes).length} field${Object.keys(changes).length === 1 ? '' : 's'} updated.`,
      });
      navigate(`/organization/${orgId}/tasks/${taskId}/view`);
    } else {
      toast({
        status: 'error',
        title: 'Failed to update task',
        description: response?.data?.detail || 'Please try again.',
      });
    }
  };

  if (loading || !formData || !task) {
    return (
      <TaskShell compact>
        <TaskSectionCard title="Loading task" description="Please wait while the task details are loading.">
          <Button variant="ghost" isLoading loadingText="Loading" />
        </TaskSectionCard>
      </TaskShell>
    );
  }

  return (
    <TaskShell compact>
      <TaskHero
        eyebrow="Edit task"
        title={task.title || 'Edit task'}
        description="Update ownership, timing, or status without losing the task’s current context."
        primaryAction={
          <TaskPrimaryButton
            leftIcon={<EditIcon />}
            onClick={() => formRef.current?.requestSubmit()}
            isLoading={saving}
          >
            Save changes
          </TaskPrimaryButton>
        }
        secondaryAction={
          <TaskSecondaryButton onClick={() => navigate(`/organization/${orgId}/tasks/${taskId}/view`)}>
            Cancel
          </TaskSecondaryButton>
        }
      />

      <form ref={formRef} onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
          <Box gridColumn={{ xl: 'span 2' }}>
            <TaskFormFields
              values={formData}
              employees={employees}
              onChange={handleChange}
              isEdit
            />
          </Box>

          <VStack align="stretch" spacing={5}>
            <TaskSectionCard
              title="Task state"
              description="A quick read of the task before you save updates."
            >
              <Stack spacing={4}>
                <HStack justify="space-between">
                  <Text color="gray.600">Status</Text>
                  <TaskStatusBadge status={formData.status} />
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Priority</Text>
                  <TaskPriorityBadge priority={formData.priority} />
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Changed fields</Text>
                  <Text fontWeight="800" color="gray.900">
                    {changedCount}
                  </Text>
                </HStack>
              </Stack>
            </TaskSectionCard>

            <TaskSectionCard
              title="Metadata"
              description="Reference context that helps you judge what should change."
            >
              <Stack spacing={4}>
                <Box>
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={1}>
                    Created by
                  </Text>
                  <Text color="gray.900">{task.created_by_name || 'Unknown'}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={1}>
                    Due date
                  </Text>
                  <Text color="gray.900">{formatTaskDate(task.due_date)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={1}>
                    Completed at
                  </Text>
                  <Text color="gray.900">{formatTaskDateTime(task.completed_at)}</Text>
                </Box>
              </Stack>
            </TaskSectionCard>
          </VStack>
        </SimpleGrid>
      </form>
    </TaskShell>
  );
};

export default EditTaskPage;
